import type { StaffAnalyticsVisitStep } from "@lafayette/schemas";

import { cn } from "../../ui/cn";
import { stepSentence } from "../event-sentences";
import { clockText } from "./events-model";

// "This visit, step by step" (Paper "Visit journey"): the steps of one visit as a timeline, oldest first, with the open event highlighted. Each
// step is the closed sentence of its event. The marker of the open event is also written ("this event"), so the highlight is never colour alone.

export function VisitTimeline({
  steps,
  openUuid,
  tz,
}: Readonly<{ steps: readonly StaffAnalyticsVisitStep[]; openUuid: string | null; tz: string }>) {
  return (
    <ol className="m-0 flex list-none flex-col p-0">
      {steps.map((step, index) => {
        const open = step.uuid === openUuid;
        const last = index === steps.length - 1;
        return (
          <li
            key={step.uuid}
            className="flex min-h-[30px] gap-2.5"
            aria-current={open ? "step" : undefined}
          >
            <span
              aria-hidden="true"
              className={cn(
                "flex w-3 shrink-0 flex-col items-center gap-1",
                open ? "pt-[5px]" : "pt-[7px]",
              )}
            >
              <span
                className={cn(
                  "shrink-0 rounded-full",
                  open
                    ? "size-3 bg-primary ring-[3px] ring-info-soft"
                    : "size-2 bg-muted-foreground",
                )}
              />
              {last ? null : <span className="w-0.5 grow bg-hairline" />}
            </span>
            <span
              className={cn(
                "flex grow gap-2.5 rounded-lg px-2 pt-0.5 pb-1.5 text-[13px] leading-[18px]",
                open ? "bg-accent font-semibold" : "",
              )}
            >
              <span className="w-[38px] shrink-0 text-xs font-normal text-muted-foreground">
                {clockText(step.timestamp, tz)}
              </span>
              <span className="text-foreground">
                {stepSentence(step.event, step.properties)}
                {open ? " · this event" : ""}
              </span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}
